// Email capture gate per brief §12.
//
// Preview state — shown when the visitor hasn't submitted an email yet:
//   - League header + the strongest 3 single-game superlatives (high score,
//     biggest blowout, closest game) above the fold
//   - Title leaderboard (top 4) and a "and 20+ more sections..." teaser
//     below, blurred + dimmed
//   - Email form overlaid with the cross-promo checkbox from §12
//
// Full state — after a successful POST to /api/email/subscribe:
//   - The wrapped children (full report from task #14) render unblurred
//   - Email persisted to localStorage so the gate doesn't re-prompt
//
// Trivia bullets are intentionally NOT generated for the preview — that
// would burn ~$0.06/visitor even for bounces. The full report (task #14)
// kicks off triviaGenerator after this gate releases.

import { useState, useEffect } from "react";
import theme from "../../data/theme";
import { captureEmailSubmitted, captureUnlockClicked } from "../../utils/recapAnalytics";

const STORAGE_KEY = "recap_email_v1";

export default function EmailGate({ pipelineState, children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [notifyForSimulator, setNotifyForSimulator] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY)) setUnlocked(true);
    } catch { /* localStorage unavailable — gate stays locked */ }
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/email/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          notifyForSimulator,
          source: pipelineState?.platform ? `recap-${pipelineState.platform}` : "recap",
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitting(false);
        setError(body.error || `Failed (${res.status})`);
        return;
      }
      captureEmailSubmitted({ email: email.trim(), crossPromoOptedIn: notifyForSimulator });
      try { localStorage.setItem(STORAGE_KEY, email.trim()); } catch { /* ignore */ }
      captureUnlockClicked();
      setUnlocked(true);
    } catch (err) {
      setSubmitting(false);
      setError(err?.message || "Network error");
    }
  };

  if (unlocked) return children;

  return <PreviewWithGate pipelineState={pipelineState} email={email} setEmail={setEmail}
    notifyForSimulator={notifyForSimulator} setNotifyForSimulator={setNotifyForSimulator}
    submit={submit} submitting={submitting} error={error} setError={setError}>
    {children}
  </PreviewWithGate>;
}

function PreviewWithGate({ pipelineState, email, setEmail, notifyForSimulator, setNotifyForSimulator, submit, submitting, error, setError, children }) {
  const stats = pipelineState?.stats || {};
  const leagueName = pipelineState?.leagueMetadata?.name || "Your League";
  const years = pipelineState?.leagueMetadata?.yearsCovered || [];
  const yearRange = years.length ? `${years[0]}–${years[years.length - 1]}` : "";

  const highScore = stats.singleGame?.highest?.[0];
  const biggestBlowout = stats.singleGame?.biggestBlowouts?.[0];
  const closestGame = stats.singleGame?.closestGames?.[0];
  const titleLeaders = Object.entries(stats.championships?.titleCounts || {})
    .map(([owner, years]) => ({ owner, count: years.length }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);

  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      fontFamily: theme.fonts.display,
      padding: "32px 20px 80px",
    }}>
      {/* League header — always crisp */}
      <div style={{ maxWidth: 760, margin: "0 auto 28px", textAlign: "center" }}>
        <div style={{
          fontSize: 11, letterSpacing: 3, color: theme.colors.brand.accent,
          textTransform: "uppercase", fontWeight: theme.fontWeight.heavy, marginBottom: 6,
        }}>League Almanac {yearRange}</div>
        <h1 style={{
          fontSize: 38, margin: 0, fontWeight: theme.fontWeight.black,
          color: theme.colors.text.primary, letterSpacing: "-1px",
        }}>{leagueName}</h1>
      </div>

      {/* Preview: highlight cards — always crisp */}
      <div style={{
        maxWidth: 760, margin: "0 auto 24px",
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12,
      }}>
        {highScore && <Highlight label="All-time high score"
          headline={`${highScore.owner} ${fmt(highScore.pts)}`}
          subline={`vs ${highScore.opp} ${fmt(highScore.oppPts)} · ${highScore.year} Wk${highScore.week}`} />}
        {biggestBlowout && <Highlight label="Biggest blowout"
          headline={`${biggestBlowout.owner} ${fmt(biggestBlowout.pts)}–${fmt(biggestBlowout.oppPts)} ${biggestBlowout.opp}`}
          subline={`margin ${fmt(biggestBlowout.margin)} · ${biggestBlowout.year} Wk${biggestBlowout.week}`} />}
        {closestGame && <Highlight label="Closest game"
          headline={`${closestGame.owner} d. ${closestGame.opp} by ${fmt(closestGame.margin)}`}
          subline={`${closestGame.year} Wk${closestGame.week}`} />}
      </div>

      {/* Title leaderboard — partly visible, then blurs into the gated content */}
      {titleLeaders.length > 0 && (
        <div style={{ maxWidth: 760, margin: "0 auto 32px" }}>
          <div style={{
            fontSize: 14, letterSpacing: 2, color: theme.colors.text.muted,
            textTransform: "uppercase", marginBottom: 10, fontWeight: theme.fontWeight.heavy,
          }}>Title Leaders</div>
          <div style={{
            background: theme.colors.bg.raised,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: 12,
            padding: 16,
          }}>
            {titleLeaders.map((row, i) => (
              <div key={row.owner} style={{
                display: "flex", justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: i < titleLeaders.length - 1 ? `1px solid ${theme.colors.border.subtle}` : "none",
                fontFamily: theme.fonts.body, fontSize: 14, color: theme.colors.text.primary,
              }}>
                <span><strong>{i + 1}.</strong> {row.owner}</span>
                <span style={{ color: theme.colors.brand.accent, fontWeight: theme.fontWeight.heavy }}>
                  {row.count} title{row.count !== 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gated section — children blurred behind email form */}
      <div style={{ position: "relative", maxWidth: 760, margin: "0 auto" }}>
        <div style={{
          filter: "blur(8px)",
          opacity: 0.35,
          pointerEvents: "none",
          userSelect: "none",
          maxHeight: 360,
          overflow: "hidden",
        }}>
          {children || <BlurredPlaceholder />}
        </div>

        {/* Gradient fade so the blur fades into the gate panel below it */}
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: 220,
          background: `linear-gradient(180deg, transparent, ${theme.colors.bg.app})`,
          pointerEvents: "none",
        }} />

        {/* Email gate panel — overlays the blurred content */}
        <div style={{
          position: "absolute", left: "50%", bottom: -12,
          transform: "translateX(-50%)",
          width: "min(100%, 500px)",
          background: theme.colors.bg.raised,
          border: `1px solid rgba(255,107,0,0.5)`,
          borderRadius: 16,
          padding: "24px 26px",
          boxShadow: theme.helpers.brandGlow(0.25),
        }}>
          <div style={{
            fontSize: 11, letterSpacing: 3, color: theme.colors.brand.accent,
            textTransform: "uppercase", fontWeight: theme.fontWeight.heavy, marginBottom: 6,
          }}>Your League Almanac is ready</div>
          <div style={{
            fontSize: 17, color: theme.colors.text.primary, fontWeight: theme.fontWeight.bold,
            marginBottom: 14,
          }}>
            Enter your email to view the full archive — every season, every draft, every record.
          </div>

          <form onSubmit={submit}>
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(null); }}
              style={{
                width: "100%", boxSizing: "border-box",
                background: theme.colors.bg.surface,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: 8, padding: "12px 14px",
                color: theme.colors.text.primary,
                fontFamily: theme.fonts.body, fontSize: 14,
                marginBottom: 10,
              }}
            />

            <label style={{
              display: "flex", alignItems: "center", gap: 8,
              fontSize: 12, color: theme.colors.text.muted,
              fontFamily: theme.fonts.body, marginBottom: 14, cursor: "pointer",
            }}>
              <input
                type="checkbox"
                checked={notifyForSimulator}
                onChange={(e) => setNotifyForSimulator(e.target.checked)}
              />
              Also notify me when the 2026 auction draft simulator opens.
            </label>

            {error && <div style={{
              background: "rgba(239,68,68,0.10)",
              border: `1px solid rgba(239,68,68,0.35)`,
              color: theme.colors.status.danger,
              borderRadius: 8, padding: "8px 12px", fontSize: 12,
              fontFamily: theme.fonts.body, marginBottom: 10,
            }}>{error}</div>}

            <button
              type="submit"
              disabled={submitting || !email.trim()}
              style={{
                width: "100%",
                background: theme.colors.brand.gradientBtn,
                color: "#fff", border: "none",
                padding: "12px 22px",
                fontSize: 13, fontWeight: theme.fontWeight.heavy,
                fontFamily: theme.fonts.display, letterSpacing: "1.5px",
                borderRadius: 10, cursor: submitting || !email.trim() ? "not-allowed" : "pointer",
                opacity: submitting || !email.trim() ? 0.55 : 1,
                transition: "opacity 0.15s",
              }}
            >
              {submitting ? "Unlocking..." : "Unlock the full almanac"}
            </button>

            <div style={{
              fontSize: 10, color: theme.colors.text.faint,
              fontFamily: theme.fonts.body, marginTop: 10, textAlign: "center",
            }}>
              We never share your address. Unsubscribe with one click.
            </div>
          </form>
        </div>

        {/* Spacer so the absolute-positioned gate panel has room below the blurred preview */}
        <div style={{ height: 360 }} />
      </div>
    </div>
  );
}

function Highlight({ label, headline, subline }) {
  return (
    <div style={{
      background: theme.colors.bg.raised,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: 12,
      padding: "14px 16px",
    }}>
      <div style={{
        fontSize: 10, letterSpacing: 2, color: theme.colors.text.muted,
        textTransform: "uppercase", fontWeight: theme.fontWeight.heavy, marginBottom: 6,
      }}>{label}</div>
      <div style={{
        fontSize: 16, color: theme.colors.text.primary, fontWeight: theme.fontWeight.bold,
        fontFamily: theme.fonts.body, marginBottom: 4,
      }}>{headline}</div>
      <div style={{
        fontSize: 12, color: theme.colors.text.muted, fontFamily: theme.fonts.body,
      }}>{subline}</div>
    </div>
  );
}

// Used when the gate is mounted standalone (no children passed). Gives the
// blurred section something to obscure so the gradient + overlay read right.
function BlurredPlaceholder() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {["Drafts", "Streaks", "Rivalries", "Records", "Awards"].map((s) => (
        <div key={s} style={{
          background: theme.colors.bg.raised, borderRadius: 10,
          padding: 16, border: `1px solid ${theme.colors.border.default}`,
        }}>
          <div style={{
            fontSize: 16, color: theme.colors.text.primary,
            fontWeight: theme.fontWeight.heavy, marginBottom: 8,
          }}>{s}</div>
          <div style={{
            height: 12, background: theme.colors.bg.surface, borderRadius: 4, marginBottom: 6,
          }} />
          <div style={{
            height: 12, background: theme.colors.bg.surface, borderRadius: 4, width: "78%", marginBottom: 6,
          }} />
          <div style={{
            height: 12, background: theme.colors.bg.surface, borderRadius: 4, width: "92%",
          }} />
        </div>
      ))}
    </div>
  );
}

function fmt(n) { return n == null ? "—" : Number(n).toFixed(1); }
